import { useState } from 'react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useAddClientDashboards, useEligibleCompanies } from '@/queries/clientDashboards'
import { extractErrorMessage } from '@/queries/ticketMutations'
import { LoadError } from './LoadError'

const clientCount = (count: number) => `${count} client${count === 1 ? '' : 's'}`

export function AddClientDialog({ trigger }: { trigger: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const eligible = useEligibleCompanies(open)
  const add = useAddClientDashboards()

  const companies = eligible.data?.data ?? []
  const visible = companies.filter(company => company.name.toLowerCase().includes(search.trim().toLowerCase()))

  function reset(nextOpen: boolean) {
    setOpen(nextOpen)
    setSearch('')
    setSelected(new Set())
    setError(null)
  }

  function toggle(companyId: string, checked: boolean) {
    setSelected(previous => {
      const next = new Set(previous)
      if (checked) next.add(companyId)
      else next.delete(companyId)
      return next
    })
  }

  async function submit() {
    setError(null)
    const companyIds = [...selected]
    try {
      await add.mutateAsync(companyIds)
      toast.success(`Added ${clientCount(companyIds.length)}`)
      reset(false)
    } catch (err) {
      setError(extractErrorMessage(err, 'Could not add these clients'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add client</DialogTitle>
          <DialogDescription>Gives each company a client dashboard. Only companies with active client users are listed.</DialogDescription>
        </DialogHeader>
        <Input pill aria-label="Search companies" placeholder="Search companies" value={search} onChange={e => setSearch(e.target.value)} />
        {eligible.isError && !eligible.data ? (
          <LoadError title="Companies didn't load" onRetry={() => eligible.refetch()} />
        ) : (
          <ul className="max-h-80 divide-y divide-border overflow-y-auto rounded-md border">
            {visible.map(company => (
              <li key={company._id} className="hover:bg-muted">
                <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    aria-label={company.name}
                    checked={selected.has(company._id)}
                    onChange={e => toggle(company._id, e.target.checked)}
                  />
                  {company.name}
                </label>
              </li>
            ))}
            {!eligible.isLoading && !visible.length && (
              <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                {companies.length ? `No companies match "${search.trim()}"` : 'No companies to add'}
              </li>
            )}
          </ul>
        )}
        {error && <p className="text-sm text-etn-danger-text">{error}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => reset(false)}>
            Cancel
          </Button>
          <Button disabled={!selected.size || add.isPending} onClick={submit}>
            {selected.size ? `Add ${clientCount(selected.size)}` : 'Add client'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
